import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  ConsentChecklistsService,
  defaultOrder,
} from './consent-checklists.service';
import Project from '../../interfaces/project';
import {
  ConsentChecklistTaskFilterInput,
  QueryGetConsentChecklistTasksOrderOrderByClause,
} from './graphql/consent-checklists';
import { debounce, pairwise, startWith, Subscription, timer } from 'rxjs';
import { formatDate } from '@angular/common';
import {
  IndicatorState,
  RequestTimingAndUrgency,
} from '../consent-requests/consent-requests.component';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import VConsentChecklistTasksComposite from '../../interfaces/v-consent-checklist-tasks-composite';
import { Person } from '../../interfaces/person';
import Organization from '../../interfaces/organization';
import ID from '../../types/id';
import { IndividualChecklistDialogComponent } from './individual-checklist-dialog/individual-checklist-dialog.component';
import { IndividualApprovalsDialogComponent } from '../consent-approvals/individual-approvals-dialog/individual-approvals-dialog.component';
import Sponsor from "../../interfaces/sponsor";

interface ChecklistTableItem {
  approval: IndicatorState;
  deal: Project | null;
  developer: Organization | null;
  sponsor: Sponsor | null;
  request: string;
  request_id: ID;
  details: string;
  timing: RequestTimingAndUrgency;
  request_status: string;
  checklist: IndicatorState;
  task: string;
  task_status: string;
  due_date: string;
  started_date: string;
  responsible_party: Person | string | null;
  notes: string;
  pastDue: boolean;
}
@Component({
    selector: 'consent-checklists',
    templateUrl: './consent-checklists.component.html',
    styleUrls: ['./consent-checklists.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConsentChecklistsComponent {
  public taskFilters = new FormGroup({
    checklist_status_id: new FormControl<string | null>(null),
    request_status_id: new FormControl<string | null>(null),
    timing_date: new FormControl<string | Date | null>(null),
    due_date: new FormControl<string | Date | null>(null),
    responsible_party: new FormControl<string | null>(null),
    project_id: new FormControl<Project | string | null>(null),
    order:
      new FormControl<QueryGetConsentChecklistTasksOrderOrderByClause | null>(
        null
      ),
  });
  public IndicatorState = IndicatorState;
  valuesSub: Subscription;
  tasksSub: Subscription;
  public columns = [
    'approval',
    'deal',
    'request',
    'details',
    'timing',
    'request_status',
    'task',
    'task_status',
    'due_date',
    'started_date',
    'responsible_party',
    'notes',
  ];
  public order: QueryGetConsentChecklistTasksOrderOrderByClause[] =
    defaultOrder;
  public currentPage: number = 1;
  public filters: ConsentChecklistTaskFilterInput | undefined;

  public data: ChecklistTableItem[] = [];
  constructor(
    private dialog: MatDialog,
    public checklistsService: ConsentChecklistsService
  ) {}
  ngOnInit() {
    this.checklistsService.getConsentChecklistTaskView();
    this.valuesSub = this.taskFilters.valueChanges
      .pipe(
        startWith(this.taskFilters.value),
        pairwise(),
        debounce(([oldVal, newVal]) =>
          oldVal.timing_date !== newVal.timing_date ? timer(500) : timer(0)
        )
      )
      .subscribe(this._parseValueChanges.bind(this));
    this.tasksSub = this.checklistsService.consentChecklistTasks$.subscribe(
      this._parseTasks.bind(this)
    );
  }
  public openApprovalDialog(consent_request_id: ID) {
    this.dialog
      .open(IndividualApprovalsDialogComponent, {
        data: { consent_request_id },
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
      })
      .afterClosed()
      .subscribe((data) => {
        this.getPage();
      });
  }
  openChecklistDialog(id: ID) {
    this.dialog
      .open(IndividualChecklistDialogComponent, {
        data: { consentRequestId: id },
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
      })
      .afterClosed()
      .subscribe((data) => {
        this.getPage();
      });
  }
  private _parseTasks(
    data: Paginator<VConsentChecklistTasksComposite, PaginatorInfo>
  ) {
    let items = [];
    for (let task of data.data) {
      let item: ChecklistTableItem = {
        approval: IndicatorState.INITIAL,
        deal: task.project
          ? {
              id: task.project_id as string,
              name: task.project,
              __typename: 'Project',
            }
          : null,
        developer: task.developer
          ? {
              id: task.developer_id as string,
              name: task.developer,
              __typename: 'Organization',
            }
          : null,
        sponsor: task.sponsor
        ? {
            id: task.sponsor_id as string,
            name: task.sponsor,
            nickname: task.sponsor,
            __typename: 'Sponsor'
          }
          : null,
        request: task.request_type,
        request_id: task.consent_request_id,
        request_status: task.request_status ?? '',
        details: task.details ?? '',
        timing: {
          is_urgent: !!task.is_urgent_flag,
          due_date: task.timing_date
            ? formatDate(task.timing_date, 'MM/dd/yyyy', 'en-US')
            : '',
          urgency_comment: task.urgency_note,
        },
        checklist: task.task_complete
          ? IndicatorState.SUCCESS
          : task.past_due
          ? task.is_urgent_flag
            ? IndicatorState.ERROR
            : IndicatorState.ALERT
          : IndicatorState.INITIAL,
        task: task.task,
        task_status: task.checklist_status,
        due_date: task.due_date
          ? formatDate(task.due_date, 'MM/dd/yyyy', 'en-US')
          : '',
        started_date: task.start_date
          ? formatDate(task.start_date, 'MM/dd/yyyy', 'en-US')
          : '',
        responsible_party:
          task.responsible_party_type === 'Person'
            ? {
                id: task.responsible_party_id as string,
                name: task.responsible_party ?? 'Unknown',
                __typename: task.responsible_party_type,
              }
            : task.responsible_party ?? '',
        notes: task.checklist_notes ?? '',
        pastDue: task.past_due && !task.task_complete,
      };
      if (task.total_approvals) {
        if (task.is_denied) {
          item.approval = IndicatorState.ERROR;
        }
        if (task.total_approvals === task.total_approved) {
          item.approval = IndicatorState.SUCCESS;
        }
      }
      items.push(item);
    }
    this.data = items;
  }
  private _parseValueChanges(data: [any, any]) {
    const [oldVal, newVal] = data;
    let hasChanged: boolean = false;
    if (newVal.order) {
      this.order = [{ ...newVal.order }];
      delete newVal.order;
      hasChanged = true;
    } else if (this.order.length === 1) {
      this.order = defaultOrder;
      hasChanged = true;
    }
    let input = newVal as unknown as ConsentChecklistTaskFilterInput;
    for (let key of Object.keys(
      input
    ) as unknown[] as (keyof ConsentChecklistTaskFilterInput)[]) {
      if (input[key] === null) {
        delete input[key];
      } else if (key === 'project_id') {
        if (typeof input[key] !== 'string') {
          input[key] = (input[key] as unknown as Project).id;
        } else {
          delete input[key];
        }
      } else if (
        typeof input[key] === 'string' &&
        (input[key] as string)?.length === 0
      ) {
        delete input[key];
      } else if (key === 'timing_date') {
        let value = input[key] as any;
        input[key] =
          value instanceof Date
            ? formatDate(value, 'yyyy-MM-dd', 'en-US')
            : null;
      }
      if (!this.filters || this.filters[key] !== input[key]) {
        hasChanged = true;
      }
    }
    if (hasChanged) {
      this.filters = Object.keys(input).length ? input : undefined;
      this.getPage();
    }
  }
  public getPage(page?: number) {
    if (!page) {
      page = this.currentPage;
    }
    this.checklistsService.getConsentChecklistTaskPage(
      page,
      this.filters,
      this.order
    );
  }
  ngOnDestroy() {
    this.checklistsService.tearDown();
    this.valuesSub?.unsubscribe();
    this.tasksSub?.unsubscribe();
  }
}
